import { Box, Flex, Link, SimpleGrid, Text, useBoolean } from '@chakra-ui/react';
import { BRAND_REDIRECT_LINK } from 'constants/links';
import { memo, useId } from 'react';
import { useTranslation } from 'react-i18next';

import { DEFAULT_SWATCH_NUM_COLS } from './ColorPicker.config';
import { BrandColorListProps, BrandColorProps, BrandColor as BrandColorType, Color } from './ColorPicker.types';
import { SwatchButton } from './ColorSwatch';

const getSwatchId = (listIdx: number, color: Color): string => `brand-color-${listIdx}-${color}`;

export const BrandColor = memo(
  ({
    brandColors,
    brandSwitchMenu,
    brandUpgrade,
    hasBrandPermission,
    onChange,
    numCols = DEFAULT_SWATCH_NUM_COLS,
    brandComponent,
  }: BrandColorProps) => {
    const { t } = useTranslation('editor_color_picker', { useSuspense: false });
    const [showUpgradeHover, setShowUpgradeHover] = useBoolean(false);
    const id = useId();
    const labelId = `brand-swatch-label-${id}`;
    const brandTitle = t('brandKit.label');

    const handleChange = (color: Color) => {
      if (showUpgradeHover && !hasBrandPermission) {
        return;
      }

      onChange(color);
    };

    const handleFocus = () => {
      if (!hasBrandPermission) {
        setShowUpgradeHover.on();
      }
    };

    const handleBlur = () => {
      setShowUpgradeHover.off();
    };

    return (
      <>
        <Flex justifyContent={'space-between'}>
          <Text fontWeight='medium' fontSize='sm' maxHeight='fit-content' marginY='auto' id={labelId}>
            {brandTitle}
          </Text>
          {brandSwitchMenu}
        </Flex>
        {!!brandColors.length && (
          <Box
            position='relative'
            maxHeight={'96px'}
            my={3}
            overflow={!hasBrandPermission ? 'hidden' : 'auto'}
            onMouseEnter={setShowUpgradeHover.on}
            onMouseLeave={setShowUpgradeHover.off}
          >
            <SimpleGrid
              key={`brand-colors`}
              data-testid={`brand-color-swatch-list`}
              spacingX={2}
              spacingY={3}
              py={1}
              columns={numCols}
              role='group'
              aria-labelledby={labelId}
            >
              <SwatchList
                colorSwatch={brandColors}
                onChange={handleChange}
                onFocus={handleFocus}
                isDisabled={!hasBrandPermission}
              />
            </SimpleGrid>
            {!hasBrandPermission && (
              <Box opacity={showUpgradeHover ? 1 : 0} onFocus={handleFocus} onBlur={handleBlur}>
                {brandUpgrade}
              </Box>
            )}
          </Box>
        )}
        {!brandColors.length ? brandComponent || <BrandKitLink /> : null}
      </>
    );
  },
);
BrandColor.displayName = 'BrandColor';

export const BrandKitLink = () => {
  const { t } = useTranslation('editor_color_picker', { useSuspense: false });
  const linkText = t('brandKit.brandKitLink');

  return (
    <Box paddingY='3'>
      <Link variant='inline' href={BRAND_REDIRECT_LINK} fontSize='xs' target='_blank' rel='noopener noreferrer'>
        {linkText}
      </Link>
    </Box>
  );
};

const SwatchList = memo(({ colorSwatch, isDisabled, onChange, onFocus }: BrandColorListProps) => {
  const { t } = useTranslation('editor_color_picker', { useSuspense: false });

  return (
    <>
      {colorSwatch.map((swatchColor: BrandColorType, idx: number) => {
        const id = getSwatchId(idx, swatchColor.color);
        const label = t('brandKit.swatchAriaLabel', { color: swatchColor.color });
        const onClick = () => onChange(swatchColor.color);

        return (
          <SwatchButton
            id={id}
            color={swatchColor.color}
            label={label}
            isDisabled={isDisabled}
            onClick={onClick}
            key={id}
            onFocus={onFocus}
          />
        );
      })}
    </>
  );
});
SwatchList.displayName = 'BrandSwatchList';
